/** Accelo company search autocomplete with debounce (FR-PAR-03). */

import { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, Chip, CircularProgress } from "@mui/material";
import { api } from "@/services/api";

interface Company {
  id: string;
  name: string;
  standing?: string;
}

interface Props {
  value: Company | Company[] | null;
  onChange: (value: Company | Company[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  pluginId?: string;
  credential?: string;
}

const STANDING_COLOR: Record<string, "success" | "default" | "error"> = {
  active: "success",
  inactive: "default",
  suspended: "error",
};

export default function CompanySearch({ value, onChange, multiple = false, disabled = false, label = "Company", helperText, pluginId, credential }: Props) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (inputValue.length < 2) {
      setOptions([]);
      setErrorMessage("");
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const data = await api.get<{ companies: Company[] }>(`/reporting/companies/search?q=${encodeURIComponent(inputValue)}${pluginId ? `&pluginId=${pluginId}` : ""}${credential ? `&credential=${credential}` : ""}`);
        setOptions(data.companies);
      } catch (err) {
        setOptions([]);
        const message = err instanceof Error ? err.message : "";
        if (message.toLowerCase().includes("credential")) {
          setErrorMessage("Search unavailable - please ask an Admin to review the plugin credential configuration.");
        } else {
          setErrorMessage("Search temporarily unavailable. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [inputValue]);

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      getOptionLabel={(o) => `${o.name} (${o.id})`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      loading={loading}
      disabled={disabled}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          helperText={errorMessage || helperText || "Type at least 2 characters to search"}
          error={!!errorMessage}
          size="small"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.name}
          <Chip
            label={option.standing ?? "unknown"}
            color={STANDING_COLOR[option.standing ?? ""] ?? "default"}
            size="small"
            sx={{ ml: 1 }}
          />
        </li>
      )}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
            color={STANDING_COLOR[option.standing ?? ""] ?? "default"}
          />
        ))
      }
    />
  );
}
